import { selectRow, selectRowByRowIndex } from '@client-shared/ui-agGrid';
import merge from 'lodash/merge';

import getTypes from './typesFactory';
import { fetchingError, showAlert } from '../../../shared/store/actions';
import alertTypes from '../../../shared/alertTypes';
import { errorNotification, successNotification } from '../../../shared/store/notificationActions';
import { ADD_OR_UPDATE_VALUES } from '../../shared/constants';

import readXlsxFileAsJson from '../../../shared/xlsxImport/readXlsxFileAsJson';

import getModalActions from '../../../shared/modals/getModalActions';

import excelFileTypeValidator from '../../../shared/xlsxImport/validators/excelFileType.validator';
import columnHeadersValidator from '../../../shared/xlsxImport/validators/columnHeaders.validator';
import currentPeriodValidator from '../../../shared/xlsxImport/validators/currentPeriod.validator';
import noEmptyRowsValidator from '../../../shared/xlsxImport/validators/noEmptyRows.validator';
import emptyRowsTrimmer from '../../../shared/xlsxImport/processors/emptyRowsTrimmer.processor';
import entityFieldsProcessor from '../../../shared/xlsxImport/processors/entityFields.processor';
import calculateImportRangeFactory from '../../../shared/xlsxImport/calculateImportRangeFactory';

import { decodeCountryTaxTypeForObject } from '../../../utils/encodeDecodeCTT';
import { byField } from '../../../utils/sorting';

const getAdminActions = (pageName, {
  api,
  postProcessors = {},
  pageTitle,
  sortingField,
  agGridConfig = {},
  decodeCountryTaxType = true,
  fetchAfterModification = null,
  excel: {
    sheetName,
    lastColumnLetter,
    dataTypesProcessor = data => data,
    columnHeaders,
    fieldMappings,
    minColumnsNumber,
    maxColumnsNumber,
    fetchDerivedData,
    derivedDataSelector,
    fieldNormalizers,
    readXlsxFileAsJsonOptionsSelector = () => Object.create(null),
  } = {},
} = {}) => {
  const {
    FETCH_ENTITIES,
    FETCH_ENTITIES_SUCCESS,
    FETCH_ENTITIES_FAILURE,
    SAVE_ENTITY,
    SAVE_ENTITY_SUCCESS,
    SAVE_ENTITY_FAILURE,
    REMOVE_ENTITY,
    REMOVE_ENTITY_SUCCESS,
    REMOVE_ENTITY_FAILURE,
    UPLOAD_ENTITIES,
    UPLOAD_ENTITIES_SUCCESS,
    UPLOAD_ENTITIES_FAILURE,
    IMPORT_ENTITIES,
    IMPORT_ENTITIES_SUCCESS,
    IMPORT_ENTITIES_FAILURE,
    CLEAR_UPLOADED_ENTITIES,
  } = getTypes(pageName);

  const getActionCreator = (apiMethod, actionCreator) => apiMethod ? actionCreator : undefined; // eslint-disable-line no-undefined

  const {
    showAction: showModalForAddEdit,
    hideAction: hideModalForAddEdit,
  } = getModalActions(`${pageName}/addEdit`);

  const {
    showAction: showModalForRemove,
    hideAction: hideModalForRemove,
  } = getModalActions(`${pageName}/remove`);

  const fetchEntities = getActionCreator(api.fetchEntities, params => async dispatch => {
    dispatch({
      type: FETCH_ENTITIES,
    });

    try {
      const response = await api.fetchEntities(params);

      return dispatch({
        type: FETCH_ENTITIES_SUCCESS,
        entities: response,
      });
    } catch (error) {
      dispatch({
        type: FETCH_ENTITIES_FAILURE,
      });
      if (error.isAuthorizationError) {
        return;
      }
      if (error.isFetchingError) {
        dispatch(fetchingError());
        dispatch(errorNotification(error.message));
        return;
      }
      throw error;
    }
  });

  const saveEntity = getActionCreator(api.updateEntity || api.addEntity,
    (entity, openForEdit, { params, callback, keepOpenAfterSaving = false } = {}) => async (dispatch, getState) => {
      dispatch({
        type: SAVE_ENTITY,
      });

      try {
        if (openForEdit) {
          await api.updateEntity(entity, params);
          if (fetchAfterModification) {
            await dispatch(fetchAfterModification.actionCreator(fetchAfterModification.dataSelector(getState())));
          }
          dispatch(successNotification(`${pageTitle} updated`));
        } else {
          await api.addEntity(entity, params);
          if (fetchAfterModification) {
            await dispatch(fetchAfterModification.actionCreator(fetchAfterModification.dataSelector(getState())));
          }
          dispatch(successNotification(`${pageTitle} added`));
        }
        if (!keepOpenAfterSaving) {
          dispatch(hideModalForAddEdit());
        }
        await dispatch(fetchEntities(entity));
        dispatch(selectRow({
          name: agGridConfig.name,
          fields: agGridConfig.fields,
          data: decodeCountryTaxType && entity.countryTaxType && entity.countryTaxType.indexOf('-') >= 0
            ? decodeCountryTaxTypeForObject(entity)
            : entity,
        }));
        dispatch({
          type: SAVE_ENTITY_SUCCESS,
        });

        if (callback) {
          return callback();
        }
      } catch (error) {
        dispatch({
          type: SAVE_ENTITY_FAILURE,
        });

        if (error.isFetchingError) {
          dispatch(fetchingError());
          dispatch(errorNotification(error.message));
          return;
        }
        throw error;
      }
    });

  const removeEntity = getActionCreator(api.removeEntity, (params, { rowIndex }) => async (dispatch, getState) => {
    dispatch({ type: REMOVE_ENTITY });

    try {
      await api.removeEntity(params);
      if (fetchAfterModification) {
        await dispatch(fetchAfterModification.actionCreator(fetchAfterModification.dataSelector(getState())));
      }
      dispatch(successNotification(`${pageTitle} removed successfully`));
      dispatch(hideModalForRemove());
      await dispatch(fetchEntities(params));
      dispatch(selectRowByRowIndex(rowIndex ? rowIndex - 1 : 0));
      dispatch({ type: REMOVE_ENTITY_SUCCESS });
    } catch (error) {
      if (error.isFetchingError) {
        dispatch(fetchingError());
        dispatch(errorNotification(error.message));
        return dispatch({ type: REMOVE_ENTITY_FAILURE });
      }
      throw error;
    }
  });

  const uploadEntitiesFromFile = getActionCreator(api.validateEntities, (
    { file, ...validationData },
    apiParams = {},
    additionalValidators = [currentPeriodValidator]
  ) =>
    async (dispatch, getState) => {
      dispatch({
        type: UPLOAD_ENTITIES,
      });

      const optionsFromState = readXlsxFileAsJsonOptionsSelector(getState());

      const options = merge({
        columnHeaders,
        minColumnsNumber,
        maxColumnsNumber,
        fieldMappings,
        fieldNormalizers,
        lastColumnLetter,
      }, optionsFromState);

      try {
        const uploadedEntities = await readXlsxFileAsJson({
          file,
          fileValidators: [excelFileTypeValidator],
          prevalidators: [columnHeadersValidator],
          postvalidators: [noEmptyRowsValidator, ...additionalValidators],
          fieldMappings: options.fieldMappings,
          sheetName,
          getRange: calculateImportRangeFactory(options.lastColumnLetter),
          preprocessors: [
            emptyRowsTrimmer,
            entityFieldsProcessor(Object.values(options.fieldMappings)),
            dataTypesProcessor,
          ],
          mapToJsonOptions: {
            header: options.columnHeaders,
          },
          validationData: {
            columnHeaders: options.columnHeaders,
            ...validationData,
            minColumnsNumber: options.minColumnsNumber,
            maxColumnsNumber: options.maxColumnsNumber,
          },
          fieldNormalizers: options.fieldNormalizers,
        });

        const validationErrors = await api.validateEntities(uploadedEntities, apiParams);

        if (fetchDerivedData) {
          await dispatch(fetchDerivedData());
        }

        const extendedEntities = postProcessors.extend
          ? await postProcessors.extend(
            uploadedEntities,
            derivedDataSelector ? derivedDataSelector(getState()) : null
          )
          : uploadedEntities;

        if (validationErrors) {
          Object.keys(validationErrors).forEach(errorIndex => {
            extendedEntities[errorIndex].validationData = validationErrors[errorIndex];
          });
        }

        const sortedEntities = extendedEntities.sort(byField(sortingField));

        dispatch({
          type: UPLOAD_ENTITIES_SUCCESS,
          uploadedEntities: sortedEntities,
        });
      } catch (error) {
        if (error.isValidationError) {
          dispatch(showAlert(error.message, alertTypes.DANGER));
        } else {
          dispatch(errorNotification(error.message));
        }
        dispatch({
          type: UPLOAD_ENTITIES_FAILURE,
        });
      }
    });

  const importEntities = getActionCreator(api.importEntities, (entities, params) => async (dispatch, getState) => {
    dispatch({ type: IMPORT_ENTITIES });

    const filteredEntities = entities
      .filter(entity => entity.addOrUpdate !== ADD_OR_UPDATE_VALUES.NO_CHANGE);

    try {
      await api.importEntities(filteredEntities, params);
      if (fetchAfterModification) {
        await dispatch(fetchAfterModification.actionCreator(fetchAfterModification.dataSelector(getState())));
      }
      dispatch(successNotification('File Upload Successful'));

      dispatch({
        type: IMPORT_ENTITIES_SUCCESS,
      });
    } catch (error) {
      dispatch({ type: IMPORT_ENTITIES_FAILURE });
      dispatch(showAlert(error.message, alertTypes.DANGER));
    }
  });

  const clearUploadedEntities = () => ({
    type: CLEAR_UPLOADED_ENTITIES,
  });

  return {
    showModalForAddEdit,
    hideModalForAddEdit,
    showModalForRemove,
    hideModalForRemove,
    fetchEntities,
    saveEntity,
    removeEntity,
    uploadEntitiesFromFile,
    importEntities,
    clearUploadedEntities,
  };
};

export default getAdminActions;
